import * as React from "react";
import {
	ChevronLeftIcon,
	ChevronRightIcon,
	MoreHorizontalIcon,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

function Pagination({ className, ...props }: React.ComponentProps<"nav">) {
	return (
		<nav
			role="navigation"
			aria-label="pagination"
			data-slot="pagination"
			className={cn("mx-auto flex w-full justify-center", className)}
			{...props}
		/>
	);
}

function PaginationContent({
	className,
	...props
}: React.ComponentProps<"ul">) {
	return (
		<ul
			data-slot="pagination-content"
			className={cn("flex flex-row items-center gap-1", className)}
			{...props}
		/>
	);
}

function PaginationItem(props: React.ComponentProps<"li">) {
	return <li data-slot="pagination-item" {...props} />;
}

type PaginationLinkProps = {
	readonly isActive?: boolean;
	readonly disabled?: boolean;
} & React.ComponentProps<typeof Button>;

function PaginationLink({
	className,
	isActive,
	size = "icon",
	...props
}: PaginationLinkProps) {
	return (
		<Button
			aria-current={isActive ? "page" : undefined}
			data-slot="pagination-link"
			data-active={isActive}
			variant={isActive ? "outline" : "ghost"}
			size={size}
			className={cn(
				"transition-none", // Que el cambio sea instantáneo
				className,
			)}
			{...props}
		/>
	);
}

function PaginationPrevious(
	props: React.ComponentProps<typeof PaginationLink>,
) {
	return (
		<PaginationLink
			aria-label="Ir a la página anterior"
			size="default"
			{...props}
		>
			<ChevronLeftIcon />
			<span className="hidden sm:block">Anterior</span>
		</PaginationLink>
	);
}

function PaginationNext(props: React.ComponentProps<typeof PaginationLink>) {
	return (
		<PaginationLink
			aria-label="Ir a la página siguiente"
			size="default"
			{...props}
		>
			<span className="hidden sm:block">Siguiente</span>
			<ChevronRightIcon />
		</PaginationLink>
	);
}

function PaginationEllipsis({
	className,
	...props
}: React.ComponentProps<"span">) {
	return (
		<span
			aria-hidden
			data-slot="pagination-ellipsis"
			className={cn("flex size-9 items-center justify-center", className)}
			{...props}
		>
			<MoreHorizontalIcon className="size-4" />
			<span className="sr-only">More pages</span>
		</span>
	);
}

export {
	Pagination,
	PaginationContent,
	PaginationLink,
	PaginationItem,
	PaginationPrevious,
	PaginationNext,
	PaginationEllipsis,
};
